import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Activity, ShieldCheck, FlaskConical, Beaker, ClipboardList, Info,
  MapPin, Clock, ChevronLeft, CheckCircle2, XCircle, Leaf, Zap, 
  Microscope, Dna, ShieldAlert, FileDown, Layers, Search, Settings,
  Scale, FileSearch, BookOpen, Database, FlaskConicalOff
} from 'lucide-react';
import { API_BASE_LOCAL_URL } from '../../../config';
import ImageCarousel from "./ImageCarousel";
import { sectionsSchema } from "./ProductDataSheets/ProductSpecificationForm";

const API_BASE = `${API_BASE_LOCAL_URL}/api/products`;

export default function ProductPage() {
  const { productCode } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("specs");

  useEffect(() => {
    let isMounted = true;
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    setLoading(true);
    axios.get(`${API_BASE}/${productCode}`, { headers })
      .then((res) => {
        if (isMounted) {
          setProduct(res.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err.response?.data?.message || "Failed to load product details.");
          setLoading(false);
        }
      });
    return () => { isMounted = false; };
  }, [productCode]);

  if (loading) return <LoadingSpinner />;
  if (error || !product) return <ErrorState error={error} navigate={navigate} />;

  return (
    <div className="bg-light min-vh-100 pb-5" style={{ fontSize: '0.75rem' }}>
      {/* 1. COMPACT TOP NAV */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white border-bottom sticky-top shadow-sm py-1">
        <div className="container-fluid px-4">
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-link text-secondary p-1" onClick={() => navigate(-1)}><ChevronLeft size={18} /></button>
            <div className="vr mx-2" style={{ height: '20px' }}></div>
            <div>
              <h6 className="fw-bold mb-0" style={{ fontSize: '0.9rem' }}>{product.productName}</h6>
              <div className="d-flex gap-2" style={{ fontSize: '0.65rem' }}>
                <span className="text-primary fw-bold">{product.productCode}</span>
                <span className="text-muted">| {product.productCategory || "Product"}</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container-fluid py-3 px-lg-5">
        <div className="row g-3">
          {/* LEFT: Identity & Matrix */}
          <div className="col-xl-3">
            <IdentityCard product={product} />
            <ComplianceMatrix product={product} />
          </div>

          {/* RIGHT: High-Density Technical Tabs */}
          <div className="col-xl-9">
             {/* NEW: Product Classification Header */}
             <div className="card border-0 shadow-sm rounded-3 mb-3 bg-white">
                <div className="card-body p-2 border-start border-primary border-4 bg-primary bg-opacity-10">
                  <div className="d-flex align-items-center gap-3">
                    <div className="bg-primary bg-opacity-20 p-2 rounded text-primary"><Layers size={16} /></div>
                    <div>
                      <h6 className="extra-small fw-bold text-muted uppercase mb-0">Classification Type</h6>
                      <p className="mb-0 fw-bold text-dark" style={{ fontSize: '0.75rem' }}>{product.type?.join(' | ') || "Not Classified"}</p>
                    </div>
                  </div>
                </div>
              </div>

            <div className="card border-0 shadow-sm rounded-3 bg-white overflow-hidden">
              <div className="card-header bg-white p-0 border-bottom">
                <div className="nav nav-tabs border-0 px-3 pt-1">
                  <TabItem active={activeTab === "specs"} onClick={() => setActiveTab("specs")} icon={<FlaskConical size={14}/>} label="Technicals" />
                  <TabItem active={activeTab === "raw"} onClick={() => setActiveTab("raw")} icon={<Leaf size={14}/>} label="Ingredients" />
                  <TabItem active={activeTab === "process"} onClick={() => setActiveTab("process")} icon={<Settings size={14}/>} label="Process/Stability" />
                  <TabItem active={activeTab === "safety"} onClick={() => setActiveTab("safety")} icon={<ShieldCheck size={14}/>} label="Quality/Safety" />
                  <TabItem active={activeTab === "usage"} onClick={() => setActiveTab("usage")} icon={<Info size={14}/>} label="Usage" />
                </div>
              </div>

              <div className="card-body p-3">
                {activeTab === "specs" && <SpecificationsTab product={product} />}
                {activeTab === "raw" && <IngredientsTab ingredients={product.ingredients} />}
                {activeTab === "process" && <ProcessesTab processes={product.processes} />}
                {activeTab === "safety" && <SafetyTab product={product} />}
                {activeTab === "usage" && <UsageTab product={product} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- TAB 1: TECHNICAL SPECS ---
const SpecificationsTab = ({ product }) => (
  <div className="animate-fade-in vstack gap-3">
    <SectionTitle icon={<Activity size={14}/>} title="Standardization Data" />
    <DynamicTable data={product.actives} />

    <div className="row g-2">
      <div className="col-md-6">
        <div className="p-2 border rounded bg-light h-100">
          <SectionTitle icon={<Database size={14}/>} title="Physical Technicals" />
          <DataPoint label="Extraction Type" value={product.extractionType} />
          <DataPoint label="Drug-Extract Ratio" value={product.ratio} />
          <DataPoint label="Drying Method" value={product.drying} />
          <DataPoint label="Carrier/Excipients" value={product.excipients} />
          <DataPoint label="Form" value={product.form} />
        </div>
      </div>
      <div className="col-md-6">
        <SectionTitle icon={<Beaker size={14}/>} title="Organoleptic Profile" />
        <SchemaBasedTable 
          schema={sectionsSchema.organolepticProperties}
          data={product.organolepticProperties}
        />  
        </div>
    </div>

    <div className="row g-2">
        <div className="col-md-6">
            <SectionTitle icon={<Microscope size={14}/>} title="Microbiology profile" />
          <SchemaBasedTable 
            schema={sectionsSchema.microbiology}
            data={product.microbiology}
          />      
          </div>
        <div className="col-md-6">
            <SectionTitle icon={<Scale size={14}/>} title="Heavy Metals Control" />
            <SchemaBasedTable 
              schema={sectionsSchema.heavyMetals}
              data={product.heavyMetals}
            />   
            </div>
    </div>
  </div>
);

// --- TAB 2: INGREDIENTS (ALL FIELDS) ---
const IngredientsTab = ({ ingredients }) => (
  <div className="row g-2 animate-fade-in">
    {ingredients?.map((ing, i) => (
      <div key={i} className="col-12 border rounded p-2 mb-1 bg-white shadow-none">
        <div className="d-flex justify-content-between border-bottom pb-1 mb-2">
            <span className="fw-bold text-success" style={{ fontSize: '0.75rem' }}>{ing.commonName} <small className="text-muted fw-normal">({ing.scientificName})</small></span>
            <span className="badge bg-light text-dark border extra-small">{ing.ingredientCode}</span>
        </div>

        <div className="row g-2">
        <div className="col-md-4 border-end">
          <DataPoint label="RM Code" value={ing.rmCode} />
          <DataPoint label="Organic Status" value={ing.organicStatus} />
          <DataPoint label="Part Used" value={ing.plantPartUsed} />
        </div>

        <div className="col-md-4 border-end">
          <DataPoint label="Standardized" value={ing.standardizedFor} />
          <DataPoint label="Sterilization" value={ing.sterilization} />
          <DataPoint label="Wet/Dry" value={ing.wetDry} />
        </div>

        <div className="col-md-4">
          <DataPoint label="Identity" value={ing.identity} />
          <DataPoint label="Strain" value={ing.strain} />
          <DataPoint label="Coordinates" value={ing.coordinates} />
        </div>

      </div>
      {/* PROCESSING AIDS SECTION */}

      <div className="mt-3 border-top pt-3">

        <h6 className="small fw-bold text-muted uppercase mb-3">
          Processing Aids
        </h6>

        {ing.processingAids?.length > 0 ? (

          <div className="row g-2">

            {ing.processingAids.map((aid, aidIndex) => (

              <div
                key={`aid-${i}-${aidIndex}`}
                className="col-md-4"
              >

                <div className="border rounded p-2 bg-light h-100">

                  <DataPoint
                    label="RM Code"
                    value={aid.processingAidRmCode}
                  />

                  <DataPoint
                    label="Aid Name"
                    value={aid.processingAidCommonName}
                  />

                  <DataPoint
                    label="Scientific"
                    value={aid.processingAidScientificName}
                  />

                  <DataPoint
                    label="Status"
                    value={aid.processingAidStatus}
                  />

                  <DataPoint
                    label="Plant Part"
                    value={aid.processingAidPlantPartUsed}
                  />

                  <DataPoint
                    label="Role"
                    value={aid.processingAidRole}
                  />

                  <DataPoint
                    label="Form"
                    value={aid.processingAidForm}
                  />

                  <DataPoint
                    label="Green Chem"
                    value={aid.processingAidGreenChemistry}
                  />

                </div>

              </div>

            ))}

          </div>

        ) : (

          <div className="text-muted small">
            No Processing Aids
          </div>

        )}

      </div>
        
      </div>
    ))}
  </div>
);

// --- TAB 3: PROCESSES & STABILITY (ALL FIELDS) ---
const ProcessesTab = ({ processes }) => (
  <div className="animate-fade-in vstack gap-3">
    {processes?.map((p, i) => (
      <div key={i} className="p-3 border rounded bg-white shadow-sm">
        <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
          <span className="fw-bold text-primary" style={{ fontSize: '0.75rem' }}>PROCESS CODE: {p.processCode}</span>
         <div className="d-flex gap-2">

  {p.msdsSignedUrl && (
    <a
      href={p.msdsSignedUrl}
      target="_blank"
      rel="noreferrer"
      className="btn btn-xs btn-outline-dark py-0 px-2"
      style={{ fontSize: '0.6rem' }}
    >
      MSDS
    </a>
  )}

  {p.processFlowChartSignedUrl && (
    <a
      href={p.processFlowChartSignedUrl}
      target="_blank"
      rel="noreferrer"
      className="btn btn-xs btn-outline-info py-0 px-2"
      style={{ fontSize: '0.6rem' }}
    >
      Flow Chart
    </a>
  )}

  {p.studySignedUrl && (
    <a
      href={p.studySignedUrl}
      target="_blank"
      rel="noreferrer"
      className="btn btn-xs btn-outline-success py-0 px-2"
      style={{ fontSize: '0.6rem' }}
    >
      Study
    </a>
  )}

</div>
        </div>
        
        <div className="row g-2 mb-2">
            <div className="col-md-6 bg-light p-2 rounded">
                <SectionTitle icon={<BookOpen size={12}/>} title="Specification & Risks" />
                <div className="extra-small text-dark mb-1"><strong>Description:</strong> {p.processDescription}</div>
                <div className="extra-small text-danger"><strong>Critical Steps:</strong> {p.criticalSteps}</div>
            </div>
            <div className="col-md-6">
                <SectionTitle icon={<Clock size={12}/>} title="Stability & Shelf Life" />
                <div className="row g-1">
                    <div className="col-6"><DataPoint label="Shelf Life" value={p.shelfLife} /></div>
                    <div className="col-6"><DataPoint label="Accelerated" value={p.acceleratedStability} /></div>
                    <div className="col-6"><DataPoint label="Real-Time" value={p.realTimeStability} /></div>
                    <div className="col-6"><DataPoint label="Oxidative" value={p.oxidativeStability} /></div>
                </div>
            </div>
        </div>

        <div className="row g-2 pt-2 border-top">
            <div className="col-md-3">
                <h6 className="extra-small fw-bold text-muted uppercase mb-1">HACCP Controls</h6>
                <DataPoint label="Physical" value={p.physical} />
                <DataPoint label="Chemical" value={p.chemical} />
                <DataPoint label="Biological" value={p.biological} />
            </div>
            <div className="col-md-3 border-start">
                <h6 className="extra-small fw-bold text-muted uppercase mb-1">Validations</h6>
                <DataPoint label="RM Validation" value={p.rawMaterial} />
                <DataPoint label="Process Ctrl" value={p.processControls} />
                <DataPoint label="QC Verified" value={p.qualityControl} />
            </div>
            <div className="col-md-3 border-start">
                <h6 className="extra-small fw-bold text-muted uppercase mb-1">Suitability</h6>
                <DataPoint label="Capsule" value={p.capsule} />
                <DataPoint label="Tablet" value={p.tablet} />
                <DataPoint label="Mixability" value={p.mixability} />
            </div>
            <div className="col-md-3 border-start">
                <h6 className="extra-small fw-bold text-muted uppercase mb-1">Study Context</h6>
                <DataPoint label="Study Type" value={p.studyType} />
                <DataPoint label="Significance" value={p.significance} />
                <DataPoint label="References" value={p.references} />
            </div>
        </div>
      </div>
    ))}
  </div>
);

// --- TAB 4: QUALITY & SAFETY ---
const SafetyTab = ({ product }) => (
  <div className="animate-fade-in vstack gap-3">
    <div className="row g-2">
      <div className="col-6 col-md-3"><SafetyPill label="Stability" active={product.stability} icon={<Clock size={12}/>} /></div>
      <div className="col-6 col-md-3"><SafetyPill label="Toxicity" active={product.toxicity} icon={<ShieldAlert size={12}/>} /></div>
      <div className="col-6 col-md-3"><SafetyPill label="Activity" active={product.activity} icon={<Zap size={12}/>} /></div>
      <div className="col-6 col-md-3"><SafetyPill label="Clinical" active={product.clinical} icon={<Microscope size={12}/>} /></div>
    </div>

    <div className="row g-2 mt-2">
        <div className="col-md-6">
            <SectionTitle icon={<Activity size={14}/>} title="Nutrition (per 100g)" />
            <SchemaBasedTable 
              schema={sectionsSchema.nutritionalInformation}
              data={product.nutritionalInformation}
            />     
            </div>
        <div className="col-md-6">
            <SectionTitle icon={<ShieldAlert size={14}/>} title="Allergen declaration" />
            <SchemaBasedTable 
              schema={sectionsSchema.allergenInformation}
              data={product.allergenInformation}
            />
            </div>
    </div>
  </div>
);

// --- TAB 5: USAGE & CLAIMS ---
const UsageTab = ({ product }) => (
  <div className="animate-fade-in vstack gap-3">
    <div className="row g-2">
        <div className="col-md-6">
            <div className="p-2 border rounded h-100 bg-light">
                <SectionTitle icon={<Dna size={14}/>} title="Indication & Benefits" />
                <div className="mb-2"><small className="fw-bold uppercase extra-small opacity-75">Intended Use:</small><p className="mb-1">{product.intendedUse || 'N/A'}</p></div>
                <div><small className="fw-bold uppercase extra-small opacity-75">Core Benefits:</small><p className="mb-0">{product.benefits || 'N/A'}</p></div>
            </div>
        </div>
        <div className="col-md-6">
            <div className="p-2 border rounded h-100">
                <SectionTitle icon={<Settings size={14}/>} title="Application Guideline" />
                <DataPoint label="Recommended Use" value={product.recommendedUse} />
                <DataPoint label="Directions" value={product.directionsForUse} />
                <DataPoint label="Recommendation" value={product.recommendation} />
                <div className="mt-2 border-top pt-2">
                    <span className="extra-small fw-bold text-muted uppercase d-block mb-1">Regulatory Status</span>
                    <div className="d-flex flex-wrap gap-1">
                          {product.productCompliance?.map((c, i) => {
                            const label =
                              typeof c === "string"
                                ? c
                                : c?.complianceItem || "Unknown";

                            return (
                              <span
                                key={i}
                                className="badge bg-dark-subtle text-dark border extra-small fw-normal"
                              >
                                {label}
                              </span>
                            );
                          })}
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div className="p-2 bg-warning bg-opacity-10 border-start border-warning border-3 rounded">
        <span className="extra-small fw-bold text-warning-emphasis uppercase mb-1">Legal Disclaimer</span>
        <p className="mb-0 text-muted italic" style={{fontSize: '0.65rem'}}>{product.disclaimer}</p>
    </div>
  </div>
);

// --- SIDEBAR ATOMS ---
const IdentityCard = ({ product }) => (
  <div className="card border-0 shadow-sm rounded-3 overflow-hidden mb-3 bg-white">
    <ImageCarousel images={product.bannerImages || []} height={160} />
    <div className="card-body p-2 px-3">
        <div className="vstack gap-1">
            <InfoRow label="Origin" value={product.origin} />
            <InfoRow label="Scope" value={product.scope} />
            <InfoRow label="Sources" value={product.ingredientSources} />
        </div>
    </div>
  </div>
);

const ComplianceMatrix = ({ product }) => {
  const flags = [
    { key: 'botanicalIdentity', label: 'Botanical ID' },
    { key: 'broadSpectrum', label: 'Broad Spec.' },
    { key: 'greenChemistry', label: 'Green Chem' },
    { key: 'agriculture', label: 'Agriculture' },
    { key: 'organicStewardship', label: 'Organic' },
    { key: 'qualityByDesign', label: 'QbD' },
    { key: 'nonGmoValidation', label: 'Non-GMO' },
    { key: 'climateSmartInterventions', label: 'Climate Smart' },
    { key: 'livelihoodMission', label: 'Livelihood' },
    { key: 'igar', label: 'IGAR' },
    { key: 'qcafMonitored', label: 'QCAF' },
    { key: 'activeCellularNutrition', label: 'Cellular Nutri.' }
  ];

  return (
    <div className="card border-0 shadow-sm rounded-3 p-2 bg-white">
      <h6 className="fw-bold uppercase mb-2 px-1" style={{ fontSize: '0.6rem', color: '#64748b' }}>Technical Matrix</h6>
      <div className="row g-1 px-1">
        {flags.map(f => (
          <div key={f.key} className="col-6">
            <div className={`p-1 px-2 border rounded-1 d-flex align-items-center gap-1 ${product[f.key] ? 'bg-success bg-opacity-10 text-success border-success-subtle' : 'bg-light text-muted opacity-50'}`} style={{ fontSize: '0.55rem' }}>
              {product[f.key] ? <CheckCircle2 size={8}/> : <XCircle size={8}/>}
              <span className="text-truncate">{f.label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- HELPER COMPONENTS ---
const InfoRow = ({ label, value }) => (
  <div className="d-flex justify-content-between align-items-center py-1 border-bottom border-light">
    <span className="fw-bold text-muted" style={{ fontSize: '0.6rem' }}>{label}</span>
    <span className="fw-bold text-dark text-end" style={{ fontSize: '0.68rem' }}>{value || "—"}</span>
  </div>
);

const DataPoint = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-2">
    <span className="text-muted fw-bold uppercase" style={{ fontSize: '0.52rem', letterSpacing: '0.3px' }}>{label}</span>
    <span className="fw-bold text-dark text-end" style={{ fontSize: '0.65rem' }}>{value || "N/A"}</span>
  </div>
);

const SectionTitle = ({ icon, title }) => (
  <h6 className="fw-bold d-flex align-items-center gap-2 mb-2 uppercase" style={{ fontSize: '0.62rem', color: '#475569' }}>
    {icon} {title}
  </h6>
);

const TabItem = ({ active, onClick, icon, label }) => (
  <button 
    onClick={onClick}
    className={`nav-link border-0 px-2 py-2 d-flex align-items-center gap-1 fw-bold transition-all ${active ? 'text-success border-bottom border-success border-2 active' : 'text-muted opacity-75'}`}
    style={{ fontSize: '0.7rem' }}
  >
    {icon} {label}
  </button>
);

const SafetyPill = ({ label, active, icon }) => (
  <div className={`p-1 px-2 rounded border d-flex align-items-center gap-2 ${active ? 'bg-success bg-opacity-10 border-success-subtle text-success' : 'bg-light text-muted opacity-60'}`}>
    {icon}
    <span className="fw-bold" style={{ fontSize: '0.58rem' }}>{label}</span>
    {active ? <CheckCircle2 size={10} className="ms-auto" /> : <XCircle size={10} className="ms-auto" />}
  </div>
);

const DynamicTable = ({ data }) => {
  if (!data || data.length === 0) return <div className="text-muted italic" style={{ fontSize: '0.6rem' }}>No data defined.</div>;
  const headers = Object.keys(data[0]);
  return (
    <div className="table-responsive rounded border mb-2">
      <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.62rem' }}>
        <thead className="bg-light">
          <tr>
            {headers.map(h => <th key={h} className="text-muted border-0 fw-bold uppercase py-1" style={{ fontSize: '0.5rem' }}>{h.replace(/([A-Z])/g, ' $1')}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i}>
              {headers.map(h => <td key={h} className="fw-medium border-light py-1">{String(row[h] || '—')}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
const SchemaBasedTable = ({ schema, data }) => {
  if (!schema) return null;

  const sectionData = data || [];

  // 🔑 map row name to actual object
  const findRow = (rowName) => {
    return sectionData.find((r) =>
      Object.values(r).some((v) => v === rowName)
    );
  };

  const getValue = (rowName, key) => {
    const row = findRow(rowName);
    return row ? row[key] || "—" : "—";
  };

  return (
    <div className="table-responsive rounded border mb-2">
      <table className="table table-sm mb-0" style={{ fontSize: '0.62rem' }}>
        <thead className="bg-light">
          <tr>
            {schema.columns.map(([_, label]) => (
              <th key={label} className="fw-bold text-muted uppercase py-1">
                {label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {schema.rows.map((rowName, i) => (
            <tr key={i}>
              {/* FIXED FIRST COLUMN */}
              <td className="fw-bold">{rowName}</td>

              {/* OTHER COLUMNS */}
              {schema.columns.slice(1).map(([key]) => (
                <td key={key}>
                  {formatValue(getValue(rowName, key))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const formatValue = (val) => {
  if (val === "Yes") return <span className="text-success fw-bold">Yes</span>;
  if (val === "No") return <span className="text-danger fw-bold">No</span>;
  return val || "—";
};

const LoadingSpinner = () => (
  <div className="d-flex vh-100 align-items-center justify-content-center bg-white">
    <div className="text-center">
      <div className="spinner-border spinner-border-sm text-success mb-2"></div>
      <p className="text-muted fw-bold extra-small uppercase">Syncing Technical Matrix...</p>
    </div>
  </div>
);

const ErrorState = ({ error, navigate }) => (
  <div className="container py-5 text-center">
    <ShieldAlert size={30} className="text-danger mx-auto mb-2" />
    <h6 className="fw-bold small">{error || "Product Not Found"}</h6>
    <button className="btn btn-xs btn-dark mt-3 px-4 rounded-pill uppercase fw-bold" style={{fontSize:'0.6rem'}} onClick={() => navigate(-1)}>Return</button>
  </div>
);